import React from 'react';

/** Return the value only when it is an absolute http(s) URL with no whitespace or control characters. */
export const safeExternalLink = (value?: string | null): string | undefined => {
  if (!value || Array.from(value).some((character) => character.charCodeAt(0) <= 32 || character.charCodeAt(0) === 127)) return undefined;
  try {
    const url = new URL(value);
    return (url.protocol === 'https:' || url.protocol === 'http:') && url.hostname ? value : undefined;
  } catch {
    return undefined;
  }
};

const URL_CANDIDATE = /((?:https?:\/\/|www\.)[^\s<>]+)/gi;

// Keep sentence punctuation outside the link; allow balanced URL parentheses.
const trimTrailingPunctuation = (piece: string): string => {
  let candidate = piece.replace(/[.,;:!?'"]+$/, '');
  while (candidate.endsWith(')') && (candidate.match(/\)/g)?.length ?? 0) > (candidate.match(/\(/g)?.length ?? 0)) {
    candidate = candidate.slice(0, -1).replace(/[.,;:!?'"]+$/, '');
  }
  return candidate;
};

type LinkifyOptions = {
  linkClassName?: string;
};

/**
 * Render plain text with http(s) and www. URLs turned into links that open in a new tab.
 * Everything else stays as text, so React keeps escaping user-provided content.
 */
export const linkifyText = (text: string, { linkClassName = 'break-words underline underline-offset-2' }: LinkifyOptions = {}): React.ReactNode[] => (
  text.split(URL_CANDIDATE).map((piece, index) => {
    // split() with a capture group puts URL candidates at odd indexes.
    if (index % 2 === 0) return piece ? <React.Fragment key={index}>{piece}</React.Fragment> : null;
    const candidate = trimTrailingPunctuation(piece);
    // A bare "www." needs a real domain after it, such as www.example.org.
    const isWww = /^www\./i.test(candidate);
    const href = isWww && !/^www\.[^./?#]+\.[^./?#]/i.test(candidate) ? undefined : safeExternalLink(isWww ? `https://${candidate}` : candidate);
    if (!href) return <React.Fragment key={index}>{piece}</React.Fragment>;
    return (
      <React.Fragment key={index}>
        <a href={href} target="_blank" rel="noopener noreferrer" className={linkClassName}>{candidate}</a>
        {piece.slice(candidate.length)}
      </React.Fragment>
    );
  })
);
